import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fileUrl } from "@/lib/files";
import { REGION_LIST } from "@/lib/countries";
import { Package, Search, ChevronLeft, ChevronRight, Filter } from "lucide-react";

const PAGE_SIZE = 12;

export default function Catalog() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("");
  const [origin, setOrigin] = useState("");
  const [cert, setCert] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);

  const load = async () => {
    const params = { page, page_size: PAGE_SIZE };
    if (q) params.q = q;
    if (category) params.category = category;
    if (region) params.region = region;
    if (origin) params.origin = origin;
    if (cert) params.certification = cert;
    const { data } = await api.get("/products", { params });
    setData(data);
  };
  useEffect(() => { load(); }, [page, category, region, origin, cert]);
  useEffect(() => { api.get("/products/categories").then((r) => setCats(r.data.categories || [])).catch(()=>{}); }, []);

  const doSearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const clear = () => { setQ(""); setCategory(""); setRegion(""); setOrigin(""); setCert(""); setPage(1); };
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <DashboardLayout title={t("catalog.title")}>
      <div className="mb-8">
        <div className="overline text-primary">{t("sidebar.catalog").toUpperCase()}</div>
        <h2 className="font-heading text-3xl font-black tracking-tight text-secondary mt-2">
          {t("catalog.resultH", { count: data.total })}
        </h2>
      </div>

      <form onSubmit={doSearch} className="bg-white border border-border rounded-lg p-4 mb-6 flex flex-wrap gap-3 items-end" data-testid="catalog-filters">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="catalog-search" className="pl-9" placeholder={t("catalog.searchPh")} value={q} onChange={(e)=>setQ(e.target.value)} />
          </div>
        </div>
        <div>
          <select data-testid="catalog-category" className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={category} onChange={(e)=>{setCategory(e.target.value); setPage(1);}}>
            <option value="">{t("catalog.allCategories")}</option>
            {cats.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <select data-testid="catalog-region" className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={region} onChange={(e)=>{setRegion(e.target.value); setPage(1);}}>
            <option value="">{t("catalog.allRegions")}</option>
            {REGION_LIST.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Input data-testid="catalog-origin" placeholder={t("catalog.originPh")} value={origin} onChange={(e)=>{setOrigin(e.target.value); setPage(1);}} />
        </div>
        <div>
          <select data-testid="catalog-cert" className="h-10 px-3 rounded-md border border-input bg-background text-sm" value={cert} onChange={(e)=>{setCert(e.target.value); setPage(1);}}>
            <option value="">{t("catalog.anyCert")}</option>
            {["HACCP","ISO 22000","CE marking","Organic EU","FDA-aligned","Halal","Fair Trade"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <Button type="submit" data-testid="catalog-search-btn"><Filter className="w-4 h-4 mr-2" />{t("common.search")}</Button>
        <Button type="button" variant="ghost" data-testid="catalog-clear" onClick={clear}>{t("common.clear")}</Button>
      </form>

      {data.items.length === 0 ? (
        <div className="py-16 text-center bg-white border border-border rounded-lg" data-testid="catalog-empty">
          <Package className="w-10 h-10 text-muted-foreground mx-auto" />
          <div className="mt-3 text-secondary font-heading font-semibold">{t("catalog.empty")}</div>
          <p className="text-sm text-muted-foreground mt-1">{t("catalog.emptySub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {data.items.map((p) => (
            <Link key={p.id} to={`/buyer/products/${p.id}`} className="bg-white border border-border rounded-lg overflow-hidden card-lift" data-testid={`catalog-item-${p.id}`}>
              <div className="aspect-[16/10] bg-muted grid place-items-center overflow-hidden">
                {p.image_urls?.[0] || p.image_ids?.[0] ? <img src={p.image_urls?.[0] || fileUrl(p.image_ids[0])} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-muted-foreground" />}
              </div>
              <div className="p-5">
                <div className="text-xs overline text-muted-foreground flex items-center gap-2">
                  <span>{p.category}</span>{p.origin_country && <span className="text-muted-foreground/70">· {p.origin_country}</span>}
                </div>
                <div className="mt-1 font-heading font-bold text-secondary">{p.name}</div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-secondary font-medium">{p.price_min ? `${p.price_min}–${p.price_max || "?"} ${p.currency}/${p.unit}` : t("product.onRequest")}</div>
                  <Badge variant="outline">{t("product.moq")} {p.moq}</Badge>
                </div>
                {p.certifications?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">{p.certifications.slice(0,3).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">{c}</span>)}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between" data-testid="pagination">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="page-prev"><ChevronLeft className="w-4 h-4 mr-1" /> {t("common.prev")}</Button>
          <div className="text-sm text-muted-foreground">{t("common.page", { page, total: totalPages })}</div>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="page-next">{t("common.next")} <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      )}
    </DashboardLayout>
  );
}
